import {
  DATED_THROUGH_YEAR,
  getShowerPeaks,
  SHOWERS,
  showerById,
} from "../data/showers";
import type {
  MeteorShower,
  NightWindow,
  ShowerOnNight,
  ShowerPeak,
} from "../types";
import {
  moonIllumination,
  moonQuality,
  phaseName,
  lunarAgeDays,
  startOfLocalDay,
  toIsoDate,
} from "./moon";

/** Rolling “next quarter” used by the Windows page. */
export const QUARTER_DAYS = 90;

export function quarterEnd(from = new Date()): Date {
  const start = startOfLocalDay(from);
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() + QUARTER_DAYS);
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function isoDay(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

function typicalPeakForYear(shower: MeteorShower, year: number): ShowerPeak {
  const peakDate = isoDay(year, shower.peakMonth, shower.peakDay);
  const startYear = shower.activeStartMonth > shower.peakMonth ? year - 1 : year;
  const endYear = shower.activeEndMonth < shower.peakMonth ? year + 1 : year;
  return {
    showerId: shower.id,
    peakDate,
    activeStart: isoDay(startYear, shower.activeStartMonth, shower.activeStartDay),
    activeEnd: isoDay(endYear, shower.activeEndMonth, shower.activeEndDay),
  };
}

function peaksOverlapping(fromIso: string, toIso: string): ShowerPeak[] {
  const dated = getShowerPeaks().filter(
    (peak) => peak.activeEnd >= fromIso && peak.activeStart <= toIso,
  );
  const fromYear = Number(fromIso.slice(0, 4));
  const toYear = Number(toIso.slice(0, 4));
  const extra: ShowerPeak[] = [];
  for (let year = fromYear; year <= toYear + 1; year += 1) {
    if (year <= DATED_THROUGH_YEAR) continue;
    for (const shower of SHOWERS) {
      extra.push(typicalPeakForYear(shower, year));
    }
  }
  return [...dated, ...extra].filter(
    (peak) => peak.activeEnd >= fromIso && peak.activeStart <= toIso,
  );
}

function toOnNight(peak: ShowerPeak, dateIso: string): ShowerOnNight | null {
  const shower = showerById(peak.showerId);
  if (!shower) return null;
  return {
    id: shower.id,
    name: shower.name,
    zhr: shower.zhr,
    notes: shower.notes,
    isPeak: peak.peakDate === dateIso,
  };
}

export function showersOn(date: Date): ShowerOnNight[] {
  const iso = toIsoDate(date);
  return peaksOverlapping(iso, iso)
    .map((peak) => toOnNight(peak, iso))
    .filter((row): row is ShowerOnNight => row !== null)
    .sort((a, b) => Number(b.isPeak) - Number(a.isPeak) || b.zhr - a.zhr);
}

export function upcomingNights(days = QUARTER_DAYS, from = new Date()): NightWindow[] {
  const start = startOfLocalDay(from);
  const nights: NightWindow[] = [];
  for (let i = 0; i < days; i += 1) {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i, 21);
    const illumination = moonIllumination(date);
    nights.push({
      date: toIsoDate(date),
      weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
      illumination,
      phaseName: phaseName(illumination, lunarAgeDays(date)),
      quality: moonQuality(illumination),
      showers: showersOn(date),
    });
  }
  return nights;
}

export function nightWindowFor(date: Date): NightWindow {
  const illumination = moonIllumination(date);
  return {
    date: toIsoDate(date),
    weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
    illumination,
    phaseName: phaseName(illumination, lunarAgeDays(date)),
    quality: moonQuality(illumination),
    showers: showersOn(date),
  };
}

export function showerPeakWindows(
  from = new Date(),
  days = QUARTER_DAYS,
): Array<{
  shower: MeteorShower;
  peak: NightWindow;
  favorable: boolean;
}> {
  const start = startOfLocalDay(from);
  const startIso = toIsoDate(start);
  const endIso = toIsoDate(
    new Date(start.getFullYear(), start.getMonth(), start.getDate() + days),
  );
  const seen = new Set<string>();
  const out: Array<{
    shower: MeteorShower;
    peak: NightWindow;
    favorable: boolean;
  }> = [];

  const peaks = peaksOverlapping(startIso, endIso)
    .filter((peak) => peak.peakDate >= startIso && peak.peakDate <= endIso)
    .sort((a, b) => a.peakDate.localeCompare(b.peakDate));

  for (const row of peaks) {
    const shower = showerById(row.showerId);
    if (!shower) continue;
    const key = `${row.showerId}-${row.peakDate}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const [y, m, d] = row.peakDate.split("-").map(Number);
    const peak = nightWindowFor(new Date(y, m - 1, d, 21));
    out.push({
      shower,
      peak,
      favorable: peak.quality !== "bright",
    });
  }
  return out;
}

export function formatIllumination(illumination: number): string {
  return `${Math.round(illumination * 100)}% lit`;
}

export function groupNightsByMonth(nights: NightWindow[]): Array<{
  label: string;
  nights: NightWindow[];
}> {
  const groups: Array<{ label: string; nights: NightWindow[] }> = [];
  for (const night of nights) {
    const [y, m] = night.date.split("-").map(Number);
    const label = new Date(y, m - 1, 1).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.nights.push(night);
    else groups.push({ label, nights: [night] });
  }
  return groups;
}
